/* ============================================================
   /app/profile — employee self-edit.

   Allowlisted fields only (see api/src/sahab_api/schemas/employee.py
   ::MyCardUpdate for the boundary and why): name, contact details, links,
   locale, and the WhatsApp prefilled greeting. Title / company / slug /
   email stay admin-only — locked at invite time. Address / website are
   gone from here entirely; they're company-wide values now, not per-card.

   Photo upload posts to /api/me/photo-upload — a Pages Function (not the
   Railway API directly) that writes to R2 the same way the admin form's
   uploader does, but authenticates via the app-user bearer JWT instead of
   CF Access (see web/functions/api/me/photo-upload.ts). Crop/focal
   controls aren't exposed here yet — new uploads keep the existing crop
   values, which default to sane portrait framing.
   ============================================================ */
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { HttpError } from "../../lib/api";
import { authedFetch, authedGetJson, getAccessToken, isLoggedIn } from "../../lib/auth";

type MyCard = {
  slug: string;
  full_name: string | null;
  phone: string | null;
  linkedin_url: string | null;
  locale: string;
  prefilled_text: string | null;
  photo_url: string | null;
};

type FormState = {
  full_name: string;
  phone: string;
  linkedin_url: string;
  locale: string;
  prefilled_text: string;
  photo_url: string;
};

function toForm(card: MyCard): FormState {
  return {
    full_name: card.full_name ?? "",
    phone: card.phone ?? "",
    linkedin_url: card.linkedin_url ?? "",
    locale: card.locale,
    prefilled_text: card.prefilled_text ?? "",
    photo_url: card.photo_url ?? "",
  };
}

// "" means "clear this field" to the API (null), not "leave unchanged" --
// PATCH only sends fields the form actually has, so an empty string here
// is always an intentional clear. full_name is the exception: it's
// required, so an empty submission is blocked client-side (see onSubmit).
function toPayload(form: FormState): Record<string, string | null> {
  return {
    full_name: form.full_name.trim(),
    phone: form.phone.trim() || null,
    linkedin_url: form.linkedin_url.trim() || null,
    locale: form.locale,
    prefilled_text: form.prefilled_text.trim() || null,
    photo_url: form.photo_url.trim() || null,
  };
}

type State =
  | { kind: "loading" }
  | { kind: "ready"; slug: string; fullName: string | null; form: FormState; onboarding: boolean }
  | { kind: "onboarded" }
  | { kind: "error"; message: string }
  | { kind: "unauthenticated" };

export default function AppProfile() {
  const [state, setState] = useState<State>(
    isLoggedIn() ? { kind: "loading" } : { kind: "unauthenticated" },
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (state.kind !== "loading") return;
    let cancelled = false;
    authedGetJson<MyCard>("/api/me/card")
      .then((card) => {
        if (!cancelled) {
          setState({
            kind: "ready",
            slug: card.slug,
            fullName: card.full_name,
            form: toForm(card),
            onboarding: !card.full_name || !card.phone,
          });
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof HttpError && e.status === 401) {
          setState({ kind: "unauthenticated" });
        } else {
          setState({ kind: "error", message: "Couldn't load your card. Try again in a moment." });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (state.kind !== "ready") return;
    if (!state.form.full_name.trim()) {
      setSaveError("Name is required.");
      return;
    }
    if (state.onboarding && !state.form.phone.trim()) {
      setSaveError("Mobile is required.");
      return;
    }
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const resp = await authedFetch("/api/me/card", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toPayload(state.form)),
      });
      if (!resp.ok) throw new HttpError(resp.status, "Save failed");
      const updated = (await resp.json()) as MyCard;
      if (state.onboarding) {
        setState({ kind: "onboarded" });
        return;
      }
      setState({
        kind: "ready",
        slug: updated.slug,
        fullName: updated.full_name,
        form: toForm(updated),
        onboarding: false,
      });
      setSaved(true);
    } catch {
      setSaveError("Couldn't save — check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    if (state.kind !== "ready") return;
    setSaved(false);
    setSaveError(null);
    setState({ ...state, form: { ...state.form, [key]: value } });
  }

  if (state.kind === "unauthenticated") {
    return <Navigate to="/app/login" replace />;
  }
  if (state.kind === "onboarded") {
    // Route through the install coach screen once, right when the card is
    // ready — this is the one moment we know an employee is actively
    // engaged and hasn't seen it yet. It's also reachable anytime after via
    // the "Install app" pill on their own card.
    return <Navigate to="/app/install" replace />;
  }
  if (state.kind === "loading") {
    return (
      <main className="min-h-screen flex items-center justify-center p-8 bg-paper">
        <p className="text-ink/60 text-sm">Loading your card…</p>
      </main>
    );
  }
  if (state.kind === "error") {
    return (
      <main className="min-h-screen flex items-center justify-center p-8 bg-paper text-center">
        <p className="text-red-600 text-sm max-w-sm">{state.message}</p>
      </main>
    );
  }

  const { form, slug, fullName, onboarding } = state;

  return (
    <main className="min-h-screen px-6 pb-6 pt-safe bg-paper">
      <div className="max-w-md mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-xl font-bold text-ink">
            {onboarding ? "Set up your card" : "Edit my card"}
          </h1>
          {!onboarding && (
            <div className="flex gap-3 text-xs text-ink/60">
              <Link to="/app/leads" className="underline">
                My leads
              </Link>
              <Link to={`/${slug}`} className="underline">
                View my card
              </Link>
            </div>
          )}
        </div>
        <p className="text-ink/50 text-xs">
          {onboarding
            ? "Add your name and mobile to finish setting up your card. Your title and company are set by your admin."
            : `${fullName ?? ""} · your title and company are set by your admin.`}
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Photo">
            <PhotoUploader
              currentUrl={form.photo_url}
              onUploaded={(url) => set("photo_url", url)}
              onClear={() => set("photo_url", "")}
            />
          </Field>

          <Field label="Full name *">
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              placeholder="The way you'd like it shown"
              required
              className={inputClass}
            />
          </Field>

          <Field label={onboarding ? "Mobile *" : "Mobile"}>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+966 5X XXX XXXX"
              required={onboarding}
              className={inputClass}
            />
          </Field>

          <Field label="LinkedIn URL">
            <input
              type="url"
              value={form.linkedin_url}
              onChange={(e) => set("linkedin_url", e.target.value)}
              placeholder="https://linkedin.com/in/…"
              className={inputClass}
            />
          </Field>

          <Field label="Card language">
            <select
              value={form.locale}
              onChange={(e) => set("locale", e.target.value)}
              className={inputClass}
            >
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </Field>

          <Field label="WhatsApp prefilled message">
            <input
              type="text"
              value={form.prefilled_text}
              onChange={(e) => set("prefilled_text", e.target.value)}
              placeholder="Hi, great to connect!"
              maxLength={200}
              className={inputClass}
            />
            <p className="text-ink/40 text-xs mt-1">
              Shown to whoever taps "Chat on WhatsApp" on your card. Leave blank for the default
              greeting.
            </p>
          </Field>

          <button
            type="submit"
            disabled={saving}
            className="w-full rounded-lg bg-ink text-paper font-semibold py-3 text-sm disabled:opacity-50"
          >
            {saving ? "Saving…" : onboarding ? "Continue" : "Save"}
          </button>
          {saved && <p className="text-signal text-sm text-center">Saved.</p>}
          {saveError && <p className="text-red-600 text-sm text-center">{saveError}</p>}
        </form>
      </div>
    </main>
  );
}

const inputClass =
  "w-full rounded-lg border border-ink/15 bg-white px-4 py-3 text-sm text-ink " +
  "placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-signal";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink/60 mb-1">{label}</span>
      {children}
    </label>
  );
}

/* Employee self-serve equivalent of the admin form's PhotoUploader —
   posts to the Pages Function at /api/me/photo-upload with the app-user
   bearer token instead of the CF Access cookie the admin path relies on. */
function PhotoUploader({
  currentUrl,
  onUploaded,
  onClear,
}: {
  currentUrl: string;
  onUploaded: (url: string) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch("/api/me/photo-upload", {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
      });
      const text = await resp.text();
      if (!resp.ok) {
        let msg = text;
        try {
          msg = (JSON.parse(text) as { error?: string }).error ?? text;
        } catch {
          /* leave text as-is */
        }
        throw new Error(`HTTP ${resp.status}: ${msg}`);
      }
      const data = JSON.parse(text) as { url: string };
      onUploaded(data.url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        {currentUrl && (
          <img
            src={currentUrl}
            alt="Your card photo"
            className="w-14 h-14 rounded-full object-cover border border-ink/15"
          />
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
          }}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="rounded-lg border border-ink/15 px-4 py-2 text-sm text-ink disabled:opacity-50"
        >
          {uploading ? "Uploading…" : currentUrl ? "Replace photo" : "Upload photo"}
        </button>
        {currentUrl && !uploading && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-amber-700 underline"
          >
            Remove
          </button>
        )}
      </div>
      <p className="text-ink/40 text-xs">JPG · PNG · WEBP · up to 5 MB</p>
      {error && <p className="text-red-600 text-xs">{error}</p>}
    </div>
  );
}
